import { type JSX, type ReactNode, type RefObject, useEffect, useId, useState } from 'react';

import { ARTENNAME, type Suchart, type Verlaufseintrag } from '../api/suche';
import {
  AUFGABEN,
  type Akteziel,
  type Ampel,
  type Aufgabe,
  BERICHTSWIDGET,
  type Groesse,
  type Kachel,
  VERLAUF,
  type Veraenderung,
  type Widgetdetail,
  type Widgetliste,
  type Widgetverlauf,
  alsAnzeige,
  widgetdetail,
  zahlText,
} from '../api/uebersicht';
import { Aufklapper } from '../komponenten/Aufklapper';
import { Dialog } from '../komponenten/Dialog';
import { Benachrichtigungsliste } from './Benachrichtigungen';
import { type NamenVon, Sinnbild } from '../komponenten/Sinnbild';
import { type Einpassung, useEinpassen, zeigt } from './einpassen';

/**
 * WIDGETS MIT MEHR INHALT, IM STIL VON ENTWURF V2 (Auftrag #37, SYS-12).
 *
 * ============================================================================
 * DIE KARTE
 * ============================================================================
 *
 * Jede Karte hat eine Kopfzeile: Sinnbild (Phosphor, Duotone im Inhalt),
 * Titel, rechts ein Menü mit „Öffnen". Im Anpassen tritt an ihre Stelle der
 * Kopf des Rasters mit Griff und demselben Sinnbild; sein Menü kann dazu
 * Größe und Entfernen (Feldraster.tsx).
 *
 * EIN KLICK AUF DIE KARTE führt dorthin, wo die Zahl herkommt — wie bisher.
 * Die Karte selbst ist dafür KEINE Schaltfläche: In ihr stehen das Menü und
 * die Fälle mit ihrem eigenen Sprung, und eine Schaltfläche in einer
 * Schaltfläche gibt es nicht (WCAG 4.1.2). Die Schaltfläche ist der Titel;
 * ihre Fläche reicht per `::after` über die ganze Karte, Menü und Fälle
 * liegen darüber.
 *
 * ============================================================================
 * INHALT NACH GRÖSSE
 * ============================================================================
 *
 *   1×1  Zahl, Bezeichnung, Veränderung, Ziel-Ampel
 *   2×1  dazu der Verlauf (auch 1×2 — Platz genug für eine Linie)
 *   2×2  dazu die wichtigsten Fälle
 *
 * Entschieden wird an der LAGE des Felds — nur was gezeigt wird, lädt die
 * Karte nach (NFA-01). Unter 1024 px fließt das Raster, und dort entscheiden
 * Container Queries (stil.css), was in die tatsächliche Breite passt.
 *
 * KEIN WIDGET ROLLT (Auftrag #38): Was nicht in die Höhe passt, fällt nach
 * Wichtigkeit weg — zuerst wird die Liste kürzer, dann geht der Verlauf,
 * dann gehen die Zusatzzeilen. Unter einer gekürzten Liste steht kein
 * „+ N weitere" (Auftrag #41). Titel und Zahl bleiben. Wie viel
 * passt, misst `einpassen.ts` an der tatsächlichen Höhe. Lange Texte stehen
 * einzeilig mit „…", der volle Text im Titel (`title`).
 *
 * FARBE: ruhige Flächen der Palette. Grün und Rot tragen nur Akzente — den
 * Pfeil der Veränderung, die Marke eines Falls, die Kante einer Kachel, die
 * Aufmerksamkeit verlangt (2.10: Farbe bedeutet etwas oder sie ist Stimmung).
 */

/** Breiter als 2,2-mal die Höhe: der Verlauf steht neben der Zahl (Auftrag #38). */
export const FLACH_AB = 2.2;

/** Was eine Karte in ihrer Größe zeigt. */
export function stufen(g: Groesse): { readonly verlauf: boolean; readonly liste: boolean } {
  return { verlauf: g.breite >= 2 || g.hoehe >= 2, liste: g.breite >= 2 && g.hoehe >= 2 };
}

/** Auftrag CRM-54: Widgets zeichnen Light — ohne die graue Duotone-Fläche. */
type Zeichen = NamenVon<'light'>;

/** Das Sinnbild je Widget — dasselbe in der Karte und im Kopf des Anpassens. */
const SINNBILD: Readonly<Record<string, Zeichen>> = {
  [AUFGABEN]: 'list-checks',
  [VERLAUF]: 'clock-counter-clockwise',
  heute: 'calendar-dots',
  engpaesse: 'car',
  besetzung: 'user-switch',
  termine: 'calendar-blank',
  buchungen: 'ticket',
  angebote: 'file-text',
  auftraege: 'clipboard-text',
  kunden: 'address-book',
  wiedervorlagen: 'bell',
  forderungen: 'receipt',
  umsatz: 'coins',
  schnittstelle: 'plugs-connected',
  fehlerablage: 'heartbeat',
};

export function sinnbildFuer(kennung: string): Zeichen {
  if (kennung.startsWith(BERICHTSWIDGET)) return 'chart-bar';
  return SINNBILD[kennung] ?? 'squares-four';
}

/** Die Ampel als Wort — Farbe allein sagt einem Vorleseprogramm nichts (WCAG 1.4.1). */
const AMPELWORT: Readonly<Record<Ampel, string>> = { ruhig: '', achtung: 'Achtung', kritisch: 'dringend' };

/**
 * Auftrag CRM-135: Eine fehlende oder unbekannte Ampel ist keine Ampel. Der
 * Typ verspricht sie, aber eine ältere API, ein Musterbogen oder ein Bericht
 * ohne Zielwert (AUS-11) liefern sie nicht immer — dann zeichnet die Karte
 * ruhig, statt abzustürzen.
 */
export function ampelOderRuhig(ampel: Ampel | null | undefined): Ampel {
  return ampel === 'achtung' || ampel === 'kritisch' ? ampel : 'ruhig';
}

/**
 * Der Rahmen jeder Karte: Kopfzeile mit Sinnbild, Titel und Menü, darunter
 * der Rumpf. Ohne `beiOeffnen` führt die Karte nirgendwohin — dann ist der
 * Titel Text und es gibt kein Menü (eine Zahl, die man anklicken kann und die
 * nichts tut, ist schlimmer als eine, die man nicht anklicken kann).
 */
export function Widgetkarte({
  titel,
  sinnbild,
  ampel = 'ruhig',
  beiOeffnen,
  laedt = false,
  klasse,
  rumpf,
  rumpfklasse,
  children,
}: {
  readonly titel: string;
  readonly sinnbild: Zeichen;
  readonly ampel?: Ampel;
  readonly beiOeffnen?: (() => void) | undefined;
  readonly laedt?: boolean;
  readonly klasse?: string;
  /** Auftrag #38: der Rumpf, an dem `useEinpassen` misst. */
  readonly rumpf?: RefObject<HTMLDivElement | null>;
  readonly rumpfklasse?: string;
  readonly children: ReactNode;
}): JSX.Element {
  const id = useId();
  const marke = ampelOderRuhig(ampel);
  return (
    <section
      className={['widget', marke === 'ruhig' ? '' : `widget--${marke}`, beiOeffnen === undefined ? 'widget--still' : '', klasse ?? '']
        .filter((k) => k !== '')
        .join(' ')}
      aria-labelledby={id}
      aria-busy={laedt}
    >
      <header className="widget__kopf">
        <span className="widget__sinnbild">
          <Sinnbild gewicht="light" name={sinnbild} />
        </span>
        <h2 className="widget__titel" id={id} title={titel}>
          {beiOeffnen === undefined ? (
            titel
          ) : (
            <button type="button" className="widget__oeffnen" onClick={beiOeffnen}>
              {titel}
            </button>
          )}
        </h2>
        {beiOeffnen !== undefined && (
          <Aufklapper
            beschriftung={`Menü zu ${titel}`}
            knopf={<Sinnbild gewicht="light" name="dots-three" />}
            knopfklasse="widget__menue"
            rechtsbuendig
            eintraege={[{ text: 'Öffnen', beiWahl: beiOeffnen }]}
          />
        )}
      </header>
      <div className={`widget__rumpf${rumpfklasse === undefined ? '' : ` ${rumpfklasse}`}`} ref={rumpf}>
        {children}
      </div>
    </section>
  );
}

/** Ein Platzhalter in der Form dessen, was kommt — statt „Wird geladen …". */
export function Skelett({ art }: { readonly art: 'zahl' | 'verlauf' | 'liste' | 'karte' }): JSX.Element {
  return (
    <div className={`skelett skelett--${art}`} aria-hidden="true">
      {art === 'liste' && [0, 1, 2].map((i) => <span key={i} className="skelett__zeile" />)}
    </div>
  );
}

const PROZENT = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * Die Veränderung zum Vorzeitraum: Pfeil, Prozent, wogegen. Die Farbe folgt
 * „mehr oder weniger ist besser" aus dem Katalog der API (AUS-11) — nie der
 * Richtung allein: Mehr Stornos ist ein Pfeil nach oben in Rot.
 */
export function Veraenderungszeile({
  v,
  einheit,
  punkte = false,
}: {
  readonly v: Veraenderung;
  readonly einheit: 'anzahl' | 'euro';
  /** Bei Quoten: Prozentpunkte statt Prozent (Auftrag #26). */
  readonly punkte?: boolean;
}): JSX.Element {
  const p = v.prozent === null ? null : Number(v.prozent);
  const urteil = p === null || p === 0 ? 'gleich' : p > 0 === (v.besser === 'mehr') ? 'gut' : 'schlecht';
  const pfeil = p === null || p === 0 ? 'arrow-right' : p > 0 ? 'arrow-up-right' : 'arrow-down-right';
  const text =
    p === null
      ? `vorher ${zahlText(v.vorher, einheit)}`
      : `${p > 0 ? '+' : p < 0 ? '−' : '±'}${PROZENT.format(Math.abs(p))} ${punkte ? 'Pp.' : '%'}`;
  return (
    <p className={`veraenderung veraenderung--${urteil}`} title={`${text} ${v.gegen} (vorher ${zahlText(v.vorher, einheit)})`}>
      <Sinnbild gewicht="light" name={pfeil} klasse="veraenderung__pfeil" />
      <span className="veraenderung__wert">{text}</span>
      <span className="veraenderung__gegen">{v.gegen}</span>
      {urteil !== 'gleich' && <span className="nur-vorlesen">{urteil === 'gut' ? ', besser' : ', schlechter'}</span>}
    </p>
  );
}

/**
 * Ein kleiner Verlauf ohne Achsenballast: eine Linie mit leiser Fläche, die
 * laufende Periode gestrichelt (AUS-10: sie ist noch nicht fertig), unten
 * die erste und die letzte Periode. Unter der Maus nennt jede Periode ihren
 * Wert; für Vorleseprogramme steht die Reihe als Text im Namen des Bildes.
 */
export function Verlaufslinie({ verlauf }: { readonly verlauf: Widgetverlauf }): JSX.Element {
  const punkte = verlauf.punkte;
  const werte = punkte.map((p) => {
    const z = Number(p.wert);
    return Number.isFinite(z) ? z : 0;
  });
  const min = Math.min(0, ...werte);
  const spanne = Math.max(...werte, 0) - min || 1;
  const B = 100;
  const H = 36;
  const x = (i: number): number => (punkte.length <= 1 ? B / 2 : (i / (punkte.length - 1)) * B);
  const y = (w: number): number => H - 2 - ((w - min) / spanne) * (H - 6);
  const bis = punkte.at(-1)?.laufend === true ? punkte.length - 1 : punkte.length;
  const pfad = (von: number, nach: number): string =>
    werte
      .slice(von, nach)
      .map((w, i) => `${i === 0 ? 'M' : 'L'}${x(von + i).toFixed(2)},${y(w).toFixed(2)}`)
      .join(' ');
  const fest = pfad(0, bis);
  const laufend = bis < punkte.length ? pfad(bis - 1, punkte.length) : '';
  const alle = pfad(0, punkte.length);
  const text = (i: number): string =>
    `${punkte[i]!.bezeichnung}: ${zahlText(punkte[i]!.wert, verlauf.einheit)}${punkte[i]!.laufend ? ' (läuft noch)' : ''}`;

  return (
    <figure className="verlaufslinie">
      <figcaption className="verlaufslinie__titel" title={verlauf.titel}>
        {verlauf.titel}
      </figcaption>
      <svg
        className="verlaufslinie__bild"
        viewBox={`0 0 ${String(B)} ${String(H)}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`${verlauf.titel}: ${punkte.map((_, i) => text(i)).join(', ')}`}
      >
        {alle !== '' && <path className="verlaufslinie__flaeche" d={`${alle} L${String(B)},${String(H)} L0,${String(H)} Z`} />}
        {fest !== '' && <path className="verlaufslinie__linie" d={fest} vectorEffect="non-scaling-stroke" />}
        {laufend !== '' && (
          <path className="verlaufslinie__linie verlaufslinie__linie--laufend" d={laufend} vectorEffect="non-scaling-stroke" />
        )}
        {punkte.map((p, i) => (
          <rect
            key={p.periode}
            className="verlaufslinie__treffer"
            x={Math.max(0, x(i) - B / punkte.length / 2)}
            width={B / punkte.length}
            y={0}
            height={H}
          >
            <title>{text(i)}</title>
          </rect>
        ))}
      </svg>
      <div className="verlaufslinie__achse" aria-hidden="true">
        <span>{punkte[0]?.bezeichnung}</span>
        <span>{punkte.at(-1)?.bezeichnung}</span>
      </div>
    </figure>
  );
}

/** Die ersten Zeilen nach dem Plan — beim Messen alle. */
function nachPlan<T>(zeilen: readonly T[], plan: Einpassung | null): readonly T[] {
  return plan?.zeilen == null ? zeilen : zeilen.slice(0, plan.zeilen);
}

/** Die wichtigsten Fälle mit Sprung in die Akte (Auftrag #37); so viele, wie passen (#38). */
export function Faelle({
  liste,
  plan,
  beiAkte,
}: {
  readonly liste: Widgetliste;
  readonly plan: Einpassung | null;
  readonly beiAkte: (ziel: Akteziel) => void;
}): JSX.Element {
  const gezeigt = nachPlan(liste.eintraege, plan);
  return (
    <div className="faelle">
      <h3 className="faelle__titel" title={liste.titel}>
        {liste.titel}
      </h3>
      {liste.eintraege.length === 0 ? (
        <p className="faelle__leer">{liste.leer}</p>
      ) : (
        <ul className="faelle__liste">
          {gezeigt.map((e) => {
            const ampel = ampelOderRuhig(e.ampel);
            const voll = [e.text, e.wert === null ? null : zahlText(e.wert, e.einheit), e.zusatz].filter((t) => t !== null).join(' · ');
            const inhalt = (
              <>
                <span className="fall__marke" aria-hidden="true" />
                <span className="fall__text">
                  {e.text}
                  {ampel !== 'ruhig' && <span className="nur-vorlesen">, {AMPELWORT[ampel]}</span>}
                </span>
                {e.wert !== null && <span className="fall__wert">{zahlText(e.wert, e.einheit)}</span>}
                {e.zusatz !== null && <span className="fall__zusatz">{e.zusatz}</span>}
              </>
            );
            return (
              <li key={e.schluessel} className={`fall fall--${ampel}`} data-zeile="">
                {e.ziel === null ? (
                  <div className="fall__zeile" title={voll}>
                    {inhalt}
                  </div>
                ) : (
                  <button type="button" className="fall__zeile fall__zeile--knopf" title={voll} onClick={() => beiAkte(e.ziel!)}>
                    {inhalt}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * Lädt Verlauf und Liste eines Widgets nach — nur die Teile, die die Größe
 * zeigt, und neu, wenn die Übersicht neu gemessen wurde (`stand`).
 */
function useDetail(
  kennung: string,
  teile: { readonly verlauf: boolean; readonly liste: boolean },
  stand: string,
): Widgetdetail | 'fehler' | undefined {
  const [detail, setzeDetail] = useState<Widgetdetail | 'fehler' | undefined>(undefined);
  const { verlauf, liste } = teile;
  useEffect(() => {
    if (!verlauf && !liste) return;
    // Auftrag CRM-165: Wer die Karte verlässt oder neu misst, bricht die Anfrage ab.
    const abbruch = new AbortController();
    setzeDetail(undefined);
    widgetdetail(kennung, { verlauf, liste }, abbruch.signal)
      .then((d) => {
        if (!abbruch.signal.aborted) setzeDetail(d);
      })
      .catch(() => {
        if (!abbruch.signal.aborted) setzeDetail('fehler');
      });
    return () => abbruch.abort();
  }, [kennung, verlauf, liste, stand]);
  return detail;
}

/** Eine Kachel als Widget: die Zahl mit allem, was ihre Größe zulässt (SYS-12, Auftrag #37, #38). */
export function Kachelwidget({
  kachel,
  groesse,
  stand,
  beiBereich,
  beiAkte,
}: {
  readonly kachel: Kachel;
  readonly groesse: Groesse;
  /** Der Stand der Übersicht — misst sie neu, lädt die Karte nach. */
  readonly stand: string;
  readonly beiBereich: (kennung: string) => void;
  readonly beiAkte: (ziel: Akteziel) => void;
}): JSX.Element {
  const teile = stufen(groesse);
  const detail = useDetail(kachel.kennung, teile, stand);
  const d = typeof detail === 'object' ? detail : undefined;
  const bereich = kachel.bereich;
  const oeffnen = bereich === null ? undefined : () => beiBereich(bereich);
  // Auftrag #38: nie rollen — was nicht passt, fällt nach Wichtigkeit weg.
  /*
   * Flach steht rechts, was die Karte am meisten zeigt: die Fälle, wenn es
   * welche gibt, sonst der Verlauf. Links bleiben Zahl, Zeilen und Verlauf.
   */
  const { ref, plan, flach } = useEinpassen([kachel, detail, teile.verlauf, teile.liste], teile.verlauf ? { flachAb: FLACH_AB } : {});
  const rechts = teile.liste ? 'liste' : 'verlauf';

  return (
    <Widgetkarte
      titel={kachel.beschriftung}
      sinnbild={sinnbildFuer(kachel.kennung)}
      ampel={kachel.ampel}
      beiOeffnen={oeffnen}
      laedt={(teile.verlauf || teile.liste) && detail === undefined}
      klasse="widget--kachel"
      rumpf={ref}
      {...(flach ? { rumpfklasse: `widget__rumpf--flach widget__rumpf--rechts-${rechts}` } : {})}
    >
      <div className="widget__kennzahl" data-teil="kennzahl" data-rang="0">
        <span className="widget__zahl">{alsAnzeige(kachel)}</span>
      </div>
      {/* Auftrag CRM-135: was fehlt, gilt wie null — die Kachel zeichnet es nicht, statt abzustürzen. */}
      {kachel.veraenderung != null && zeigt(plan, 'veraenderung') && (
        <div className="widget__zeile" data-teil="veraenderung" data-rang="1">
          <Veraenderungszeile v={kachel.veraenderung} einheit={kachel.einheit} />
        </div>
      )}
      {kachel.ziel != null && zeigt(plan, 'ziel') && (
        /* AUS-11 (Auftrag #29): die Ampel gegen das Ziel, als Wort und als Marke; ohne Ampel keine Marke. */
        <p className={`zielzeile zielzeile--${ampelOderRuhig(kachel.ziel.ampel)}`} title={kachel.ziel.text} data-teil="ziel" data-rang="1">
          {kachel.ziel.text}
        </p>
      )}
      {kachel.zusatz != null && zeigt(plan, 'zusatz') && (
        <p className="widget__zusatz" title={kachel.zusatz} data-teil="zusatz" data-rang="1">
          {kachel.zusatz}
        </p>
      )}
      {teile.verlauf && zeigt(plan, 'verlauf') && (detail === undefined || d?.verlauf != null) && (
        <div className="widget__verlauf" data-teil="verlauf" data-rang="2" {...(rechts === 'verlauf' ? { 'data-nebenan': '' } : {})}>
          {detail === undefined ? <Skelett art="verlauf" /> : <Verlaufslinie verlauf={d!.verlauf!} />}
        </div>
      )}
      {teile.liste && zeigt(plan, 'liste') && (
        <div className="widget__liste" data-teil="liste" data-rang="3" data-nebenan="">
          {detail === undefined ? (
            <Skelett art="liste" />
          ) : detail === 'fehler' ? (
            <p className="faelle__leer">Die Fälle konnten nicht geladen werden.</p>
          ) : (
            d?.liste != null && <Faelle liste={d.liste} plan={plan} beiAkte={beiAkte} />
          )}
        </div>
      )}
    </Widgetkarte>
  );
}

const DRINGLICHKEITEN: readonly { readonly ampel: Ampel; readonly name: string }[] = [
  { ampel: 'kritisch', name: 'dringend' },
  { ampel: 'achtung', name: 'bald' },
  { ampel: 'ruhig', name: 'zur Kenntnis' },
];

/**
 * „Das steht an" (SYS-12): nach Dringlichkeit gruppiert, mit der Zahl je
 * Gruppe. Darunter die Aufgaben, dringende zuerst; in größeren Feldern mit
 * einer Überschrift je Gruppe. So viele, wie passen (Auftrag #38) — die Zahl
 * je Gruppe steht immer, also fehlt keine Aufgabe unbemerkt.
 *
 * EIN WEG ZUR GANZEN LISTE STATT EINES KNOPFS JE AUFGABE (Auftrag #41): Die
 * Aufgaben sind reiner Text. In der Zeile der Gruppenzahlen — sie steht
 * immer — öffnet „Alle Aufgaben" die ganze Liste mit dem Sprung je Aufgabe in
 * ihren Bereich. Bis Auftrag CRM-170 war das die Glocke der Kopfzeile; seit
 * sie nur noch die eigenen Einträge trägt (ÄA-70), klappt die Liste hier am
 * Widget auf — dieselbe Liste, die das Widget kürzt, nur vollständig.
 */
export function Aufgabenwidget({
  aufgaben,
  groesse,
  beiBereich,
}: {
  readonly aufgaben: readonly Aufgabe[];
  readonly groesse: Groesse;
  /** Der Sprung einer Aufgabe in ihren Bereich. Ohne Angabe steht „Alle Aufgaben" nicht da. */
  readonly beiBereich?: ((kennung: string) => void) | undefined;
}): JSX.Element {
  const gruppen = DRINGLICHKEITEN.map((g) => ({ ...g, aufgaben: aufgaben.filter((a) => a.dringlichkeit === g.ampel) })).filter(
    (g) => g.aufgaben.length > 0,
  );
  const gross = groesse.hoehe >= 3 || groesse.breite >= 3;
  const { ref, plan } = useEinpassen([aufgaben, gross]);
  /* Eine Folge aus Gruppenköpfen und Aufgaben — gekürzt wird sie als Ganzes. */
  const zeilen: readonly ({ readonly kopf: (typeof gruppen)[number] } | { readonly aufgabe: Aufgabe })[] = gross
    ? gruppen.flatMap((g) => [{ kopf: g }, ...g.aufgaben.map((a) => ({ aufgabe: a }))])
    : gruppen.flatMap((g) => g.aufgaben.map((a) => ({ aufgabe: a })));
  const gezeigt = nachPlan(zeilen, plan);
  /* ERR-10 (CRM-207): Benachrichtigungen einzeln, sobald „Das steht an" welche zählt. */
  const mitNachrichten = aufgaben.some((a) => a.kennung.startsWith('benachrichtigung-'));
  const [nachrichtenOffen, setzeNachrichtenOffen] = useState(false);

  return (
    <Widgetkarte titel="Das steht an" sinnbild={sinnbildFuer(AUFGABEN)} klasse="widget--aufgaben" rumpf={ref}>
      <div className="aufgaben__gruppen" data-teil="gruppen" data-rang="0">
        <ul className="dringlichkeiten" aria-label="Nach Dringlichkeit">
          {gruppen.map((g) => (
            <li key={g.ampel} className={`dringlichkeit dringlichkeit--${g.ampel}`}>
              <span className="dringlichkeit__zahl">{g.aufgaben.length}</span> {g.name}
            </li>
          ))}
        </ul>
        {beiBereich !== undefined && (
          <Aufklapper
            beschriftung="Alle Aufgaben"
            klasse="aufgaben__alleliste"
            knopfklasse="aufgaben__alle verweis--fenster"
            knopf="Alle Aufgaben"
            kopf="Das steht an"
            schliesskreuz
            eintraege={aufgaben.map((a) => ({
              schluessel: a.kennung,
              text: a.text,
              zusatz: String(a.anzahl),
              beiWahl: () => {
                if (a.bereich !== null) beiBereich(a.bereich);
              },
            }))}
            rechtsbuendig
          />
        )}
        {beiBereich !== undefined && mitNachrichten && (
          <button className="aufgaben__alle verweis--fenster" type="button" onClick={() => setzeNachrichtenOffen(true)}>
            Benachrichtigungen
          </button>
        )}
        {beiBereich !== undefined && nachrichtenOffen && (
          <Dialog titel="Benachrichtigungen" beiSchliessen={() => setzeNachrichtenOffen(false)}>
            <Benachrichtigungsliste
              beiBereich={(kennung) => {
                setzeNachrichtenOffen(false);
                beiBereich(kennung);
              }}
            />
          </Dialog>
        )}
      </div>
      {zeigt(plan, 'liste') && (
        <div className="widget__liste widget__liste--ohne-linie" data-teil="liste" data-rang="3">
          <ul className="aufgaben">
            {gezeigt.map((z) =>
              'kopf' in z ? (
                <li key={`kopf-${z.kopf.ampel}`} className="aufgaben__kopf" data-zeile="kopf">
                  <h3 className="faelle__titel">
                    {z.kopf.name} · {z.kopf.aufgaben.length}
                  </h3>
                </li>
              ) : (
                <li className={`aufgabe aufgabe--${z.aufgabe.dringlichkeit}`} key={z.aufgabe.kennung} data-zeile="">
                  <span className="fall__marke" aria-hidden="true" />
                  <span className="aufgabe__text" title={z.aufgabe.text}>
                    {z.aufgabe.text}
                  </span>
                </li>
              ),
            )}
          </ul>
        </div>
      )}
    </Widgetkarte>
  );
}

/** Das Sinnbild je Art im Verlauf — dieselben wie in der Menüzeile. */
const ARTSINNBILD: Readonly<Record<Suchart, Zeichen>> = {
  kunde: 'user',
  ansprechpartner: 'address-book',
  angebot: 'file-text',
  auftrag: 'clipboard-text',
  rechnung: 'receipt',
  termin: 'calendar-blank',
};

const UHR = new Intl.DateTimeFormat('de-DE', { timeStyle: 'short', timeZone: 'Europe/Berlin' });
const TAG = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Berlin' });
const KALENDERTAG = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' });

/** „vor 5 Min.", „14:12", „gestern", „03.09." — in Ortszeit (FRS-01). */
export function wann(iso: string, jetzt: Date = new Date()): string {
  const t = new Date(iso);
  const minuten = Math.floor((jetzt.getTime() - t.getTime()) / 60_000);
  if (minuten >= 0 && minuten < 1) return 'gerade eben';
  if (minuten >= 0 && minuten < 60) return `vor ${String(minuten)} Min.`;
  const heute = KALENDERTAG.format(jetzt);
  const tag = KALENDERTAG.format(t);
  if (tag === heute) return `${UHR.format(t)} Uhr`;
  if (tag === KALENDERTAG.format(new Date(jetzt.getTime() - 86_400_000))) return 'gestern';
  return TAG.format(t);
}

/** M-07: „Zuletzt geöffnet" — Sinnbild je Art und der Zeitpunkt; so viele, wie passen (Auftrag #38). */
export function Verlaufswidget({
  letzte,
  beiVorgang,
}: {
  readonly letzte: readonly Verlaufseintrag[];
  readonly beiVorgang: (art: Suchart, id: string) => boolean;
}): JSX.Element {
  const { ref, plan } = useEinpassen([letzte]);
  const gezeigt = nachPlan(letzte, plan);
  return (
    <Widgetkarte titel="Zuletzt geöffnet" sinnbild={sinnbildFuer(VERLAUF)} klasse="widget--verlauf" rumpf={ref}>
      {zeigt(plan, 'liste') && (
      <div className="widget__liste widget__liste--ohne-linie" data-teil="liste" data-rang="3">
        <ul className="letzte">
          {gezeigt.map((e) => {
            const zusatz = [e.zusatz, wann(e.gesehenAm)].filter((t) => t !== null).join(' · ');
            return (
              <li key={`${e.art}/${e.datensatz}`} data-zeile="">
                <button
                  className="letzte__eintrag"
                  type="button"
                  title={`${ARTENNAME[e.art]}: ${e.bezeichnung} · ${zusatz}`}
                  onClick={() => beiVorgang(e.art, e.datensatz)}
                >
                  <span className="letzte__sinnbild" aria-hidden="true">
                    <Sinnbild gewicht="light" name={ARTSINNBILD[e.art]} />
                  </span>
                  <span className="letzte__name">
                    <span className="nur-vorlesen">{ARTENNAME[e.art]}: </span>
                    {e.bezeichnung}
                  </span>
                  <span className="letzte__zusatz">{zusatz}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      )}
    </Widgetkarte>
  );
}
